import { useState, useEffect } from 'react';
import {
  CreditCard, FileText, Building2, CheckCircle, AlertTriangle,
  Loader2, Mail, LogOut, Shield, Users, Edit2, ArrowLeft, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

/**
 * LicenseBlockedPage
 * Full-screen blocking page shown when branch license is expired and >10 children.
 *
 * Admin view: Payment options (Stripe or Fakturownia)
 * Non-admin view: Message to contact administrator
 */
export default function LicenseBlockedPage({
  branch,
  billing,
  childCount,
  billingType,
  monthlyPrice,
  isAdmin,
  userRole
}) {
  const { signOut } = useAuth();
  const [actionLoading, setActionLoading] = useState(false);

  // Invoice form state (for Fakturownia billing)
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceNip, setInvoiceNip] = useState('');
  const [invoiceRegon, setInvoiceRegon] = useState('');

  // Payment method selection state - always show selection first on blocked page
  const [showPaymentSelection, setShowPaymentSelection] = useState(true);
  const [selectedBillingType, setSelectedBillingType] = useState(billingType);

  // Feedback dialog state
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [mayContact, setMayContact] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Load existing invoice data from RSPO
  useEffect(() => {
    if (branch) {
      const rspo = branch.rspo_data || {};
      setInvoiceName(rspo.name || branch.name || '');
      setInvoiceAddress([rspo.street, rspo.postal_code, rspo.city].filter(Boolean).join(', ') || '');
      setInvoiceNip(rspo.nip || '');
      setInvoiceRegon(rspo.regon || '');
    }
    if (billing?.invoice_email) {
      setInvoiceEmail(billing.invoice_email);
    }
  }, [branch, billing]);

  // Handle Stripe checkout
  const handleStripeCheckout = async () => {
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: {
          branch_id: branch.id,
          success_url: `${window.location.origin}/?license_activated=true`,
          cancel_url: `${window.location.origin}/?license_cancelled=true`
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      logger.error('Stripe checkout error:', error);
      toast.error(error.message || 'Błąd tworzenia sesji płatności');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle invoice subscription confirmation
  const handleConfirmInvoice = async () => {
    if (!invoiceEmail) {
      toast.error('Podaj adres email do faktur');
      return;
    }
    if (!invoiceName || !invoiceNip) {
      toast.error('Uzupełnij nazwę placówki i NIP');
      return;
    }

    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('confirm-invoice-subscription', {
        body: {
          branch_id: branch.id,
          invoice_email: invoiceEmail,
          invoice_name: invoiceName,
          invoice_address: invoiceAddress,
          invoice_nip: invoiceNip,
          invoice_regon: invoiceRegon
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('Subskrypcja aktywowana! Faktura zostanie wysłana na podany email.');
      // Redirect to home
      window.location.href = '/';
    } catch (error) {
      logger.error('Invoice confirmation error:', error);
      toast.error(error.message || 'Błąd potwierdzania danych do faktury');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle selecting Stripe as payment method
  const handleSelectStripe = async () => {
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('branches')
        .update({ billing_type: 'stripe' })
        .eq('id', branch.id);
      if (error) throw error;
      setSelectedBillingType('stripe');
      setShowPaymentSelection(false);
      // Proceed with Stripe checkout
      handleStripeCheckout();
    } catch (error) {
      logger.error('Error setting billing type:', error);
      toast.error('Błąd ustawiania metody płatności');
      setActionLoading(false);
    }
  };

  // Handle selecting Invoice as payment method
  const handleSelectInvoice = async () => {
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('branches')
        .update({ billing_type: 'invoice' })
        .eq('id', branch.id);
      if (error) throw error;
      setSelectedBillingType('invoice');
      setShowPaymentSelection(false);
    } catch (error) {
      logger.error('Error setting billing type:', error);
      toast.error('Błąd ustawiania metody płatności');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle feedback submission
  const handleSubmitFeedback = async () => {
    if (!feedbackReason) {
      toast.error('Wybierz powód rezygnacji');
      return;
    }
    if (mayContact && !contactEmail) {
      toast.error('Podaj adres email do kontaktu');
      return;
    }

    setFeedbackSubmitting(true);
    try {
      const { error } = await supabase.rpc('submit_license_feedback', {
        p_branch_id: branch.id,
        p_reason: feedbackReason,
        p_feedback_text: feedbackText || null,
        p_may_contact: mayContact,
        p_contact_email: mayContact ? contactEmail : null
      });

      if (error) throw error;

      setFeedbackSubmitted(true);
      setShowFeedbackDialog(false);
      toast.success('Dziękujemy za opinię!');
    } catch (error) {
      logger.error('Feedback submission error:', error);
      toast.error(error.message || 'Błąd wysyłania opinii');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      logger.error('Logout error:', error);
    }
  };

  // Get role display text
  const getRoleDisplayText = () => {
    switch (userRole) {
      case 'teacher': return 'nauczyciela';
      case 'parent': return 'rodzica';
      default: return 'użytkownika';
    }
  };

  // Non-admin view
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <Shield className="w-10 h-10 text-red-600" />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Licencja wygasła
            </h1>

            <p className="text-slate-600 mb-6">
              Okres testowy dla placówki <strong>{branch?.name}</strong> zakończył się.
              Dostęp {getRoleDisplayText()} jest tymczasowo zablokowany.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">
                    Co mogę zrobić?
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    {userRole === 'parent'
                      ? 'Skontaktuj się z przedszkolem w sprawie aktywacji subskrypcji.'
                      : 'Skontaktuj się z administratorem placówki w celu aktywacji subskrypcji.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left text-sm text-slate-600">
              <p className="font-medium text-slate-900 mb-2">Twoje dane są bezpieczne</p>
              <p>
                Wszystkie dane pozostają zapisane i będą dostępne po aktywacji subskrypcji przez administratora.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Wyloguj się
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin view - Payment options
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Header Card */}
        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 mb-1">
                  Okres testowy wygasł
                </h1>
                <p className="text-slate-600">
                  Aktywuj subskrypcję, aby przywrócić dostęp do platformy dla Ciebie oraz wszystkich nauczycieli i rodziców.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Branch & Pricing Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{branch?.name}</p>
                <p className="text-sm text-slate-500">Placówka</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-2xl font-bold text-slate-900">
                  <Users className="w-5 h-5 text-slate-500" />
                  {childCount}
                </div>
                <p className="text-xs text-slate-500">dzieci</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">
                  {monthlyPrice} PLN
                </div>
                <p className="text-xs text-slate-500">miesięcznie</p>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-3 text-center">
              Cena: 4 PLN za dziecko (max 200 PLN). Placówki do 10 dzieci korzystają za darmo.
            </p>
          </CardContent>
        </Card>

        {/* Payment Method Selection */}
        {showPaymentSelection ? (
          <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
            <CardHeader>
              <CardTitle className="text-indigo-900">Wybierz metodę płatności</CardTitle>
              <CardDescription>
                Wybierz preferowaną metodę, aby aktywować subskrypcję.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Stripe Option */}
                <button
                  onClick={handleSelectStripe}
                  disabled={actionLoading}
                  className="relative group p-6 text-left border-2 border-slate-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50/50 transition-all disabled:opacity-50"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl text-white">
                      <CreditCard className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 group-hover:text-indigo-900">
                        Szybkie płatności
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Karta płatnicza lub BLIK
                      </p>
                      <ul className="mt-3 space-y-1 text-xs text-slate-500">
                        <li className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Automatyczne odnowienie
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Natychmiastowa aktywacja
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Bezpieczne płatności Stripe
                        </li>
                      </ul>
                    </div>
                  </div>
                  {actionLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-xl">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    </div>
                  )}
                </button>

                {/* Invoice Option */}
                <button
                  onClick={handleSelectInvoice}
                  disabled={actionLoading}
                  className="relative group p-6 text-left border-2 border-slate-200 rounded-xl hover:border-green-400 hover:bg-green-50/50 transition-all disabled:opacity-50"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 group-hover:text-green-900">
                        Przelew tradycyjny
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Faktura VAT co miesiąc
                      </p>
                      <ul className="mt-3 space-y-1 text-xs text-slate-500">
                        <li className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Faktura na email
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          14 dni na płatność
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Dane z rejestru RSPO
                        </li>
                      </ul>
                    </div>
                  </div>
                  {actionLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-xl">
                      <Loader2 className="w-6 h-6 animate-spin text-green-600" />
                    </div>
                  )}
                </button>
              </div>

              {/* Invoice commitment notice */}
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  <strong>Ważne:</strong> Wybierając przelew tradycyjny, zobowiązujesz się do opłacenia faktury w terminie 14 dni od jej wystawienia. Faktura zostanie wygenerowana po zakończeniu miesiąca rozliczeniowego.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        ) : selectedBillingType === 'stripe' ? (
          /* Stripe Payment */
          <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
            <CardHeader>
              <CardTitle className="text-indigo-900">Aktywuj subskrypcję</CardTitle>
              <CardDescription>
                Dodaj kartę płatniczą, aby aktywować subskrypcję.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPaymentSelection(true)}
                disabled={actionLoading}
                className="text-slate-600 hover:text-slate-900 -ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Zmień metodę płatności
              </Button>

              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                onClick={handleStripeCheckout}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Przejdź do płatności
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Invoice Payment */
          <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardHeader>
              <CardTitle className="text-green-900">Potwierdź dane do faktury</CardTitle>
              <CardDescription>
                Sprawdź i uzupełnij dane placówki do wystawiania faktur.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPaymentSelection(true)}
                disabled={actionLoading}
                className="text-slate-600 hover:text-slate-900 -ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Zmień metodę płatności
              </Button>

              {/* Invoice Data Form */}
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-slate-900">Dane do faktury</h4>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Edit2 className="w-3 h-3" />
                    Możesz edytować
                  </span>
                </div>

                <div className="grid gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="invoice-name" className="text-xs text-slate-500">Nazwa</Label>
                    <Input
                      id="invoice-name"
                      value={invoiceName}
                      onChange={(e) => setInvoiceName(e.target.value)}
                      placeholder="Nazwa placówki"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="invoice-address" className="text-xs text-slate-500">Adres</Label>
                    <Input
                      id="invoice-address"
                      value={invoiceAddress}
                      onChange={(e) => setInvoiceAddress(e.target.value)}
                      placeholder="Ulica, kod pocztowy, miasto"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="invoice-nip" className="text-xs text-slate-500">NIP</Label>
                      <Input
                        id="invoice-nip"
                        value={invoiceNip}
                        onChange={(e) => setInvoiceNip(e.target.value)}
                        placeholder="1234567890"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="invoice-regon" className="text-xs text-slate-500">REGON (opcjonalnie)</Label>
                      <Input
                        id="invoice-regon"
                        value={invoiceRegon}
                        onChange={(e) => setInvoiceRegon(e.target.value)}
                        placeholder="123456789"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Email */}
              <div className="space-y-2">
                <Label htmlFor="invoice-email" className="text-sm font-medium">
                  Email do faktur
                </Label>
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="invoice-email"
                    type="email"
                    placeholder="faktury@przedszkole.pl"
                    value={invoiceEmail}
                    onChange={(e) => setInvoiceEmail(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Na ten adres będą wysyłane faktury VAT
                </p>
              </div>

              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                onClick={handleConfirmInvoice}
                disabled={actionLoading || !invoiceEmail || !invoiceName || !invoiceNip}
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Potwierdź dane i aktywuj subskrypcję
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Data Safety Notice */}
        <Alert className="border-blue-200 bg-blue-50">
          <Shield className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Twoje dane są bezpieczne.</strong> Wszystkie dane pozostają zapisane i będą dostępne natychmiast po aktywacji subskrypcji.
          </AlertDescription>
        </Alert>

        {/* Feedback and Logout Options */}
        <div className="text-center space-y-3">
          {!feedbackSubmitted ? (
            <Button
              variant="outline"
              onClick={() => setShowFeedbackDialog(true)}
              className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Nie chcesz subskrypcji? Powiedz nam dlaczego
            </Button>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg border border-green-200">
              <CheckCircle className="w-4 h-4" />
              Dziękujemy za opinię!
            </div>
          )}
          <div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-slate-500 hover:text-slate-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Wyloguj się
            </Button>
          </div>
        </div>

        {/* Feedback Dialog */}
        <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-600" />
                Twoja opinia jest dla nas ważna
              </DialogTitle>
              <DialogDescription>
                Powiedz nam, dlaczego nie chcesz kontynuować subskrypcji. Pomoże nam to ulepszyć platformę.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Reason selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Powód rezygnacji</Label>
                <RadioGroup value={feedbackReason} onValueChange={setFeedbackReason}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="too_expensive" id="reason-price" />
                    <Label htmlFor="reason-price" className="font-normal cursor-pointer">
                      Cena jest za wysoka
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="missing_features" id="reason-features" />
                    <Label htmlFor="reason-features" className="font-normal cursor-pointer">
                      Brakuje potrzebnych funkcji
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="switching_platform" id="reason-switching" />
                    <Label htmlFor="reason-switching" className="font-normal cursor-pointer">
                      Przechodzę na inne oprogramowanie
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="closing_branch" id="reason-closing" />
                    <Label htmlFor="reason-closing" className="font-normal cursor-pointer">
                      Placówka zostaje zamknięta
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="technical_issues" id="reason-technical" />
                    <Label htmlFor="reason-technical" className="font-normal cursor-pointer">
                      Zbyt wiele problemów technicznych
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="not_user_friendly" id="reason-ux" />
                    <Label htmlFor="reason-ux" className="font-normal cursor-pointer">
                      Trudna obsługa platformy
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="other" id="reason-other" />
                    <Label htmlFor="reason-other" className="font-normal cursor-pointer">
                      Inny powód
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Additional comments */}
              <div className="space-y-2">
                <Label htmlFor="feedback-text" className="text-sm font-medium">
                  Dodatkowe uwagi (opcjonalnie)
                </Label>
                <Textarea
                  id="feedback-text"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Jakie funkcje by Ci pomogły? Co moglibyśmy zrobić lepiej?"
                  rows={3}
                  className="resize-none"
                />
              </div>

              {/* Contact permission */}
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="may-contact"
                    checked={mayContact}
                    onCheckedChange={(checked) => setMayContact(checked === true)}
                    className="mt-0.5"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label htmlFor="may-contact" className="font-normal cursor-pointer text-sm">
                      Możecie się ze mną skontaktować
                    </Label>
                    <p className="text-xs text-slate-500">
                      Chętnie porozmawiamy o tym, jak możemy pomóc
                    </p>
                  </div>
                </div>

                {mayContact && (
                  <Input
                    type="email"
                    placeholder="Twój email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="mt-2"
                  />
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowFeedbackDialog(false)}
                disabled={feedbackSubmitting}
              >
                Anuluj
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                disabled={feedbackSubmitting || !feedbackReason}
              >
                {feedbackSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wysyłanie...
                  </>
                ) : (
                  'Wyślij opinię'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
